import { clsx } from "clsx";
import { Fieldset, Radio, RadioGroup } from "@headlessui/react";
import FormLabel from "./FormLabel";
import { generateId } from "../../../Utils";
import InputError from "./InputError";
import { useTranslation } from "react-i18next";

// "solid" fills the selected option with the accent, which reads the same as a
// primary button. "segmented" puts the options in a track and lifts the
// selected one as a white pill, so the accent is left to mean "the result".
const SURFACE_STYLES = {
  solid: "gap-3",
  // Radius is concentric: the p-1 track inset from rounded-inner (8px) leaves
  // 4px for the pill, which is why the options use rounded, not rounded-md.
  segmented:
    "gap-1 rounded-inner bg-slate-100 p-1 ring-1 ring-inset ring-slate-900/5",
};

const OPTION_STYLES = {
  solid:
    "rounded-md bg-white text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50 data-[checked]:bg-blue-600 data-[checked]:text-white data-[checked]:ring-0 data-[checked]:hover:bg-blue-700 data-[focus]:ring-offset-2 [&:not([data-focus],[data-checked])]:ring-inset",
  // The white pill on the slate track is only 1.10:1, far below the 3:1 a fill
  // needs to carry a state on its own, so the weight change is what marks the
  // selection. Do not drop data-[checked]:font-semibold.
  segmented:
    "rounded text-slate-600 hover:text-slate-900 data-[checked]:bg-white data-[checked]:font-semibold data-[checked]:text-slate-900 data-[checked]:shadow-[0_1px_2px_rgba(15,23,42,0.10),0_0_0_1px_rgba(15,23,42,0.06)] data-[focus]:ring-offset-2 data-[focus]:ring-offset-slate-100",
};

const FormRadioGroup = ({
  label,
  className,
  disabled,
  options,
  value,
  onChange,
  name,
  error,
  required,
  hint,
  verticalSpace = "py-3",
  gridClassName = "grid-cols-2",
  variant = "solid",
}) => {
  const { t } = useTranslation();

  const id = name || generateId();

  const hasError =
    error?.messages?.length ||
    error?.message ||
    (Array.isArray(error) ? error.length : error);

  return (
    <Fieldset
      aria-label={label ? t(label) : undefined}
      disabled={disabled}
      className={clsx("data-[disabled]:opacity-50", className)}
    >
      {Boolean(label) && (
        <FormLabel htmlFor={id} required={required} hint={hint}>
          {t(label)}
        </FormLabel>
      )}
      <RadioGroup
        value={value}
        onChange={onChange}
        className={clsx(
          "grid",
          SURFACE_STYLES[variant],
          gridClassName,
          Boolean(label) ? "mt-1" : "",
        )}
      >
        {options.map((option, index) => (
          <Radio
            key={option.name || option.label || index}
            value={option}
            disabled={option.disabled}
            className={clsx(
              option.disabled
                ? "cursor-not-allowed opacity-40"
                : "cursor-pointer focus:outline-none active:scale-[0.98] motion-reduce:transform-none",
              "flex h-full items-center justify-center px-3 text-sm sm:flex-1",
              // box-shadow stays out of the transition list. Chrome will not resolve a
              // var-composed box-shadow while it is being transitioned, which silently
              // drops the data-[focus] ring on both variants.
              "transition-[background-color,color,transform] duration-150 ease-out",
              "data-[focus]:ring-2 data-[focus]:ring-blue-600",
              verticalSpace,
              OPTION_STYLES[variant],
            )}
          >
            {t(option.label)}
          </Radio>
        ))}
      </RadioGroup>
      {hasError && <InputError id={id} error={error} />}
    </Fieldset>
  );
};

export default FormRadioGroup;
